
import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Papa from 'papaparse';
import { CSVLink } from 'react-csv'
import axios from 'axios'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { deleteReport, getReports } from '../../api/reports'
import { getAllUsers, getUserAnalytics } from '../../api/user'
import { snakeToCamel } from './AdminEditRelease'
import { CircularProgress, Alert } from '@mui/material'

const AdminReportsHistory = () => {

    const navigate = useNavigate()
    const queryClient = useQueryClient()
    // State to track which report is being deleted
    const [deletingReportId, setDeletingReportId] = useState(null);

    const {data: reports = [], isLoading: reportsLoading, error:reportsError} = useQuery({
        queryKey: ['report_metadata'],
        queryFn: getReports,
        select: (data) => data.data.map(d=> snakeToCamel(d)),
    })
    const {data: users = [], isLoading: userLoading, error: userError} = useQuery({
        queryKey: ["users"],
        queryFn: getAllUsers,
        select: (data) => data.map(user => snakeToCamel(user))
    })
    


    function parseCsv(csvText) {
        const rows = csvText.trim().split('\n');
        return rows.map(row => row.split(','));
    }

    const updateOverview = (month_year) => {
        const monthyear = month_year.split("/")
        let ans = month_year
        if(monthyear.length > 1){
            ans = monthyear[1] + "-" + (monthyear[0].length == 1 ? "0" + monthyear[0] : monthyear[0])
        }
        navigate(`details/${ans}`)
    }


    const successBtn = useRef(null)

    const deleteReportsMonth = (month, revenue) => {
        const [analyticsMonth, analyticsYear] = month.split('/')
        const analyticsMonthNumber = analyticsMonth * 1 < 10 ? `0${analyticsMonth}` : analyticsMonth
        const userReportsMonthYear = `${analyticsYear}-${analyticsMonthNumber}`
        
        // Set the deleting state for this report
        setDeletingReportId(month);

        // Call the deleteReport API function
        deleteReport(userReportsMonthYear)
            .then(() => {
                // Invalidate the queries after successful deletion
                queryClient.invalidateQueries(['report_metadata'])
                queryClient.invalidateQueries(["admin_reports", "67bac6ae0017d8ab983f047e"])
                
                // Show success message
                successBtn.current.click()
            })
            .catch(error => {
                console.error("Error deleting report:", error)
                // Handle error if needed
            })
            .finally(() => {
                 // Reset the deleting state regardless of success or error
                 setDeletingReportId(null);
            });
    }

    const getFileData = (url, fileID) => {
        axios.get(url)
        .then(res => {
            Papa.parse(res.data, {
                header: true, 
                complete: (result) => {
                    const payeeReports = result.data
                    for (let i = 0; i < payeeReports.length; i++) {
                        const reportUPC = payeeReports[i]['UPC'].replace('\'', '') * 1;
                        let reportPayee = ''
                        users.forEach(user => {
                            let hasUPC = false
                            if (user.releases) {
                                user.releases.forEach(release => {
                                    if (release.upc * 1 === reportUPC) {
                                            hasUPC = true
                                        }
                                    })
                                }
                                if (hasUPC === true) {
                                    if (user.payeeID) {
                                        reportPayee = user.payeeID
                                    }
                                }
                        })
                        payeeReports[i]['Payee ID'] = reportPayee
                    }
                    handleDownload(payeeReports, fileID)
                }
            });
        })
    }

    const convertToCSV = (data) => {
        const headers = Object.keys(data[0]);
        const csvRows = [
            headers.join(','), // header row first
            ...data.map(row => headers.map(header => JSON.stringify(row[header] || '')).join(','))
        ];
        return csvRows.join('\n');
    };
    
    const handleDownload = (data, fileID) => {
        const csvData = convertToCSV(data);
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', fileID);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

  return (
    <div className="admin-page py-5">
        <div className='container'>
            <h1 className='dash-title'>Uploaded Reports</h1>
            <div className="row">
                <div className="col-12 px-1 mt-4">
                    <div className="dash-card">
                        <div className="dash-card-heading">
                            <h4>Reports</h4>
                        </div>
                        <div className="table-responsive mt-4">
                            <table className="table dash-table">
                                <thead>
                                    <tr>
                                        <td>Month</td>
                                        <td>Streams</td>
                                        <td>Revenue</td>
                                        <td># of Lines</td>
                                        <td>Action</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Loading State */}
                                    {reportsLoading && (
                                        <tr>
                                            <td colSpan="5">
                                                <div style={{ display: 'flex', justifyContent: 'center', padding: '50px 0' }}>
                                                    <CircularProgress />
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {/* Error State */}
                                    {reportsError && (
                                        <tr>
                                            <td colSpan="5">
                                                <Alert 
                                                    severity="error" 
                                                    sx={{ margin: '20px 0' }}
                                                >
                                                    {reportsError.message || 'An error occurred while fetching reports data'}
                                                </Alert>
                                            </td>
                                        </tr>
                                    )}

                                    {/* Empty State */}
                                    {!reportsLoading && !reportsError && reports.length === 0 && (
                                        <tr>
                                            <td colSpan="5">
                                                <Alert 
                                                    severity="info" 
                                                    sx={{ margin: '20px 0' }}
                                                >
                                                    No reports history found
                                                </Alert>
                                            </td>
                                        </tr>
                                    )}

                                    {/* Data Table */}
                                    {!reportsLoading && !reportsError && reports.length > 0 && 
                                        reports.map((report, i) => (
                                            <tr key={i}>
                                                <td><div className='table-item'>{report.monthYear}</div></td>
                                                <td><div className='table-item'>{report.totalStreams}</div></td>
                                                <td><div className='table-item'>${(report.totalPrice * 1).toFixed(2)}</div></td>
                                                <td><div className='table-item'>{report.rows}</div></td>
                                                <td>
                                                    <div className='table-item d-flex align-items-center'>
                                                        <i 
                                                            className="fi fi-rr-eye table-action-btn a-blue me-2"
                                                            onClick={() => updateOverview(report.monthYear)}
                                                        ></i>
                                                        <i 
                                                            className="fi fi-rr-download table-action-btn a-green me-2"
                                                            onClick={() => getFileData(report.fileUrl, report.monthYear)}
                                                        ></i>
                                                        {/* Conditional rendering for delete button/spinner */}
                                                        {deletingReportId === report.monthYear ? (
                                                            <CircularProgress size={18} sx={{ color: '#dc3545' }} />
                                                        ) : (
                                                            <i 
                                                                className="fi fi-rr-trash table-action-btn a-red"
                                                                onClick={() => deleteReportsMonth(report.monthYear, report.totalPrice)}
                                                                style={{ cursor: deletingReportId ? 'not-allowed' : 'pointer' }}
                                                            ></i>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>


        </div>


        <button data-bs-toggle="modal" data-bs-target="#successModal" ref={successBtn} hidden></button>
        <div className="modal fade" id="successModal" tabindex="-1" data-bs-backdrop="static" aria-labelledby="successModalLabel" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-body success-modal">
                        <button className='success-close-btn' data-bs-dismiss="modal" style={{ border: 'none', background: 'none', padding: 0 }}>
                            <i className="fi fi-rr-cross"></i>
                        </button>
                        <i className="fi fi-rr-check-circle text-success success-icon"></i>
                        <h5>Reports Deleted Successfully!</h5>
                    </div>
                </div>
            </div>
        </div>


    </div>
  )
}

export default AdminReportsHistory